import { Autocomplete, Box, FormControl, FormControlLabel, FormHelperText, InputLabel, Link, MenuItem, OutlinedInput, Radio, RadioGroup, Select, Stack, TextField, Typography } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import EmissionService from '../../../shared/services/emission/emission.service'
import { IEmissionDetail, IEditEmission } from '../../../shared/services/emission/emission.model'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model'
import LocationService from '../../../shared/services/location/location.service'
import { ICountry, ILocation } from '../../../shared/services/location/location.model'
import ProjectService from '../../../shared/services/project/project.service'
import { IProject } from '../../../shared/services/project/project.model'
import ContactSupplierService from '../../../shared/services/contact-supplier/contact-supplier.service'
import { IContactSupplierDetails } from '../../../shared/services/contact-supplier/contact-supplier.model'
import { SupplierPicker } from '../../presentation-components/form-field-components/supplier-picker'
import VehicleService from '../../../shared/services/vehicle/vehicle.service'
import { IVehicleDetails } from '../../../shared/services/vehicle/vehicle.model'
import RegulatedEmissionTradingSchemeService from '../../../shared/services/regulated-emission-trading-scheme/regulated-emission-trading-scheme.service'
import { IRegulatedEmissionTradingScheme } from '../../../shared/services/regulated-emission-trading-scheme/regulated-emission-trading-scheme.model'
import CapitalGoodService from '../../../shared/services/capital-good/capital-good-service'
import { ICapitalGood } from '../../../shared/services/capital-good/capital-good.model'
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked'
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service'
import { ICustomField } from '../../../shared/services/custom-field/custom-field.model'
import { resolveEmissionFieldFlags } from '../../../shared/utils/emission-field-config'
import EmissionCalculationService from '../../../shared/services/emission-calculation/emission-calculation.service'
import { FLAG_CATEGORY_OPTIONS, FLAG_NO_CATEGORY_VALUE, flagFieldsFromSelectValue, flagSelectValueFrom } from '../../../shared/constants/flag-emissions'
import { useFlagEmissionsCapability } from '../../../hooks/use-flag-emissions-capability'
import { formatActivityDisplay, resolveCategoryEditMode, resolveFlagFromCalculations } from '../../../shared/utils/emission-summary-utils'
import { notifyError } from '../../../shared/utils/notify-error'
import { useDirtyBaseline } from '../../../hooks/use-dirty-baseline'
import { formatConversionCodeLabel } from '../../../shared/utils/conversion-utils'
import { SpendCategoryPicker } from '../../presentation-components/emissons-components/overview/emissions-input/spend-category-picker'
import { EmissionsFilterLogic } from '../../presentation-components/emissons-components/overview/emissions-filter/emissions-filter-logic'
import { EditOutlined } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import moment from 'moment'

export const EditEmissionDialog: FunctionComponent<{
  open: boolean
  dialogDetailsId: string | undefined
  closeHandler: () => void
  successHandler: () => void
}> = ({ open, closeHandler, dialogDetailsId, successHandler }) => {
  const emissionService = new EmissionService()
  const emissionCalculationService = new EmissionCalculationService()
  const subsidiaryService = SubsidiaryService.getInstance()
  const locationService = LocationService.getInstance()
  const projectService = ProjectService.getInstance()
  const contactSupplierService = ContactSupplierService.getInstance()
  const vehicleService = VehicleService.getInstance()
  const regulatedEmissionTradingSchemeService = new RegulatedEmissionTradingSchemeService()
  const capitalGoodService = new CapitalGoodService()
  const customFieldService = new CustomFieldService()

  const userNotificationsContext = useContext(UserNotificationsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const screenLabelsContext = useContext(ScreenLabelsContext)

  const fieldConfig = userDetails?.organisation?.emissionFieldConfig
  const isHidden = (key: string) =>
    fieldConfig?.[key as keyof typeof fieldConfig] === 'hidden'
  const getMode = (key: string): 'mandatory' | 'optional' =>
    fieldConfig?.[key as keyof typeof fieldConfig] === 'mandatory' ? 'mandatory' : 'optional'

  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
  const [countries, setCountries] = useState<ICountry[]>()
  const [locations, setLocations] = useState<ILocation[]>()
  const [projects, setProjects] = useState<IProject[]>()
  const [contactSuppliers, setContactSuppliers] = useState<IContactSupplierDetails[]>()
  const [vehicles, setVehicles] = useState<IVehicleDetails[]>()
  const [capitalGoods, setCapitalGoods] = useState<ICapitalGood[]>()
  const [schemes, setSchemes] = useState<IRegulatedEmissionTradingScheme[]>()
  const [dialogDetails, setDialogDetails] = useState<IEmissionDetail>()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [showEts, setShowEts] = useState<boolean>(false)
  const [customFields, setCustomFields] = useState<ICustomField[]>([])
  /** FLAG as loaded, so the extra calculation request only fires when the user actually changes it. */
  const [initialFlag, setInitialFlag] = useState<{ isFlag: boolean; flagCategory: string | null }>({
    isFlag: false,
    flagCategory: null,
  })

  /** Set once the user opens the category picker, so an untouched dialog never sends a factor change. */
  const [isEditingCategory, setIsEditingCategory] = useState<boolean>(false)

  const fieldFlags = useMemo(
    () => resolveEmissionFieldFlags(fieldConfig, customFields),
    [fieldConfig, customFields]
  )
  const mandatoryCustomFields = fieldFlags.visibleCustomFields.filter(cf =>
    fieldFlags.mandatoryCustomFieldIds.includes(cf.id)
  )
  const optionalCustomFields = fieldFlags.visibleCustomFields.filter(cf =>
    !fieldFlags.mandatoryCustomFieldIds.includes(cf.id)
  )

  const configurableKeys = ['country', 'location', 'supplier', 'vehicle', 'product', 'project', 'ets', 'excludeFromReporting']
  const subsidiaryVisible = !!(subsidiaries && (userDetails?.organisation?.enableSubsidiaries || (subsidiaries?.length ?? 0) > 1))
  const hasMandatory = subsidiaryVisible || mandatoryCustomFields.length > 0 || configurableKeys.some(k => !isHidden(k) && getMode(k) === 'mandatory')
  const hasOptional = optionalCustomFields.length > 0 || configurableKeys.some(k => !isHidden(k) && getMode(k) === 'optional')

  const isReportPeriodLocked = useIsReportPeriodLocked()
  const { flagEnabled } = useFlagEmissionsCapability()

  // ── Category editing ──────────────────────────────────────────────
  // Which factor picker (if any) this emission supports, and the calculation it currently sits on.
  const categoryEditMode = useMemo(
    () => resolveCategoryEditMode(dialogDetails?.emissionCalculations),
    [dialogDetails]
  )
  const primaryCalculation = useMemo(
    () => dialogDetails?.emissionCalculations?.find(
      (calculation) => calculation.type !== 'MEASUREMENT' && calculation.isSelected && !calculation.isWtt
    ),
    [dialogDetails]
  )
  /** The emission's own year, so the factor list matches the period being reported. */
  const categoryCalendarYear = useMemo(
    () => (dialogDetails?.startDate ? moment(dialogDetails.startDate).format('YYYY') : ''),
    [dialogDetails]
  )
  /**
   * For spend the exact activity is what is being changed, so show that rather than the emission's
   * activity label, which for spend falls back to the category or even the supplier name.
   */
  const currentCategoryLabel = useMemo(() => {
    if (categoryEditMode === 'SPEND' && primaryCalculation?.spendBasedConversion) {
      return formatConversionCodeLabel(primaryCalculation.spendBasedConversion)
    }
    return formatActivityDisplay(dialogDetails?.activityLabel, dialogDetails?.activityContext)
  }, [categoryEditMode, primaryCalculation, dialogDetails])

  // Its own form: these fields only matter while the category is being changed, so keeping them off
  // the main schema leaves the emission form untouched for everyone who never opens the picker.
  const categoryFormik = useFormik<Record<string, any>>({
    initialValues: {
      conversionSourceId: '',
      returnId: '',
      groupId: '',
      categoryId: '',
      measurementUnitId: '',
      measurementUnitName: '',
      detailLevelOne: '',
      detailLevelTwo: '',
      detailLevelThree: '',
      detailLevelFour: '',
      detailLevelFive: '',
    },
    onSubmit: () => undefined,
  })

  /**
   * Open the picker where the emission already is. Left empty, it asked for a conversion factor
   * source before it would show a category at all, so changing a category began by re-answering a
   * question the record had already answered.
   */
  const startCategoryEdit = () => {
    const conversionSourceId = primaryCalculation?.conversion?.conversionSourceId
    if (conversionSourceId) categoryFormik.setFieldValue('conversionSourceId', conversionSourceId)
    if (primaryCalculation?.groupId) categoryFormik.setFieldValue('groupId', primaryCalculation.groupId)
    if (primaryCalculation?.categoryId) categoryFormik.setFieldValue('categoryId', primaryCalculation.categoryId)
    setIsEditingCategory(true)
  }

  /** Back out of the category change alone, leaving every other edit in the dialog intact. */
  const cancelCategoryEdit = () => {
    categoryFormik.resetForm()
    setIsEditingCategory(false)
  }

  /**
   * The factor-change payload, or null when there is nothing to send: the picker was never opened,
   * no factor was chosen, or the choice matches what the emission already uses.
   */
  const buildCategoryPayload = (): Record<string, unknown> | null => {
    if (!isEditingCategory || !categoryEditMode) return null
    const { conversionSourceId, returnId } = categoryFormik.values
    if (!conversionSourceId || !returnId) return null
    if (returnId === primaryCalculation?.conversionId) return null

    if (categoryEditMode === 'SPEND') {
      return { type: 'SPEND', conversionSourceId, spendBasedConversionId: returnId }
    }
    return {
      type: 'ACTIVITY',
      groupId: categoryFormik.values.groupId,
      categoryId: categoryFormik.values.categoryId,
      conversionSourceId,
      returnId,
      detailLevelOne: categoryFormik.values.detailLevelOne || null,
      detailLevelTwo: categoryFormik.values.detailLevelTwo || null,
      detailLevelThree: categoryFormik.values.detailLevelThree || null,
      detailLevelFour: categoryFormik.values.detailLevelFour || null,
      detailLevelFive: categoryFormik.values.detailLevelFive || null,
      measurementUnitId: categoryFormik.values.measurementUnitId || null,
      calendarYear: categoryCalendarYear || null,
    }
  }

  const editEmissionSchema = useMemo(() => {
    const mandatory = (key: string) =>
      fieldConfig?.[key as keyof typeof fieldConfig] === 'mandatory'
    const req = (key: string, label: string) =>
      mandatory(key) ? Yup.string().required(`${label} is required`) : Yup.string().nullable()
    const customFieldShape: Record<string, Yup.AnySchema> = {}
    customFields.forEach((cf) => {
      customFieldShape[cf.id] = fieldFlags.mandatoryCustomFieldIds.includes(cf.id)
        ? Yup.string().required(`${cf.fieldName} is required`)
        : Yup.string().nullable()
    })
    return Yup.object().shape({
    subsidiaryId: Yup.string().required(
      `You must provide a ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`
    ),
    countryId: Yup.string().required('You must provide a country').nullable(),
    locationId: req('location', 'Location'),
    projectId: req('project', 'Project'),
    contactSupplierId: req('supplier', 'Supplier'),
    vehicleId: req('vehicle', 'Vehicle'),
    capitalGoodId: req('product', 'Product / Capital Good'),
    regulatedEmissionTradingSchemeId: Yup.string().nullable(),
    excludeFromReporting: Yup.boolean(),
    excludeFromReportingReason: Yup.string().when('excludeFromReporting', {
      is: true,
      then: (schema) => schema.required('You must provide a reason for exclusion'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    isFlag: Yup.boolean(),
    flagCategory: Yup.string().nullable(),
    ...customFieldShape,
  })
  }, [fieldConfig, screenLabelsContext, customFields, fieldFlags])

  const formik = useFormik<IEditEmission & { partOfEts?: boolean } & Record<string, any>>({
    initialValues: {
      locationId: null,
      countryId: null,
      subsidiaryId: null,
      projectId: null,
      contactSupplierId: null,
      vehicleId: null,
      capitalGoodId: null,
      regulatedEmissionTradingSchemeId: null,
      excludeFromReporting: false,
      excludeFromReportingReason: '',
      partOfEts: false,
      isFlag: false,
      flagCategory: '',
    },
    validationSchema: editEmissionSchema,
    onSubmit: (values) => {
      if (isReportPeriodLocked) {
        userNotificationsContext.openDialog({
          active: true,
          title: 'Action Not Allowed',
          message: `The selected report period is locked and cannot be modified`,
          buttonCopy: 'Ok',
          hideCancelButton: true,
        })
        return
      }
      if (!dialogDetailsId) return
      const { partOfEts, isFlag, flagCategory, ...rest } = values
      const customFieldValues = customFields.map((cf) => ({
        customFieldId: cf.id,
        value: rest[cf.id] === '' || rest[cf.id] === undefined ? null : rest[cf.id],
      }))
      const emission: Partial<IEditEmission> & {
        customFieldValues?: { customFieldId: string; value: string | null }[]
      } = {
        locationId: rest.locationId,
        countryId: rest.countryId,
        subsidiaryId: rest.subsidiaryId,
        projectId: rest.projectId,
        contactSupplierId: rest.contactSupplierId,
        vehicleId: rest.vehicleId,
        capitalGoodId: rest.capitalGoodId,
        regulatedEmissionTradingSchemeId: rest.regulatedEmissionTradingSchemeId,
        excludeFromReporting: rest.excludeFromReporting,
        excludeFromReportingReason: rest.excludeFromReportingReason,
        ...(customFieldValues.length > 0 && { customFieldValues }),
      }
      // FLAG lives on the emission's calculations, so it needs its own request; only send it when changed.
      const nextFlagCategory = isFlag ? (flagCategory || null) : null
      const flagChanged =
        flagEnabled && (isFlag !== initialFlag.isFlag || nextFlagCategory !== initialFlag.flagCategory)

      const categoryPayload = buildCategoryPayload()
      // The category form's values as submitted, for the post-save baseline below.
      const submittedCategoryValues = categoryFormik.values

      setIsSubmitting(true)
      emissionService
        .editSelectedEmissions([dialogDetailsId], emission)
        .then(() =>
          flagChanged
            ? emissionCalculationService.updateSelectedFlagClassification([dialogDetailsId], {
                isFlag: !!isFlag,
                flagCategory: nextFlagCategory,
              })
            : undefined
        )
        // A factor change rewrites the calculations, so it is a separate request from the emission
        // PATCH. A bulk of one lets the backend pick the affected calculations and re-derive the units.
        .then(() =>
          categoryPayload
            ? emissionCalculationService
                .bulkEditSelectedEmissionCalculations([dialogDetailsId], categoryPayload)
                .then((response) => {
                  // With a single id the request can still succeed while skipping the emission, so
                  // the reason has to be read off the response rather than a rejection.
                  const skippedReason = response.skipped?.[0]?.reason
                  if (skippedReason) throw new Error(skippedReason)
                })
            : undefined
        )
        .then(() => {
          successHandler()
          setIsSubmitting(false)
          userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' })
          // Baseline exactly the values that were submitted, so keystrokes typed while
          // the save was in flight stay dirty.
          rebaseline(() => ({ emission: values, category: submittedCategoryValues }))
        })
        .catch((err) => {
          if (err.response?.data?.code === 1) {
            userNotificationsContext.openSnackbar({
              open: true,
              message: 'Report period is locked',
              backgroundColor: 'text.primary',
            })
            console.error(err)
          } else if (!err.response && err?.message) {
            // `err.message` carries the per-emission skip reason, which arrives on a 200 response.
            userNotificationsContext.openSnackbar({
              open: true,
              message: err.message,
              backgroundColor: 'text.primary',
            })
            console.error(err)
          } else {
            notifyError(userNotificationsContext.openSnackbar, err, 'saving the emission')
          }
          setIsSubmitting(false)
        })
    },
  })

  // Dirty tracking across both forms. formik.dirty is unusable here: the emission is
  // seeded via setValues/setFieldValue after load, which marks the form dirty on open.
  const { isDirty, rebaseline } = useDirtyBaseline({
    emission: formik.values,
    category: categoryFormik.values,
  })

  /** Reset the main form to the emission's saved server state. */
  const seedFormFromEmission = (emission: IEmissionDetail) => {
    const { isFlag: flagged, flagCategory } = resolveFlagFromCalculations(emission.emissionCalculations)
    setInitialFlag({ isFlag: flagged, flagCategory })
    formik.setValues({
      locationId: emission.locationId ?? null,
      countryId: emission.countryId ?? null,
      subsidiaryId: emission.subsidiaryId ?? null,
      projectId: emission.projectId ?? null,
      contactSupplierId: emission.contactSupplierId ?? null,
      vehicleId: emission.vehicleId ?? null,
      capitalGoodId: emission.capitalGoodId ?? null,
      regulatedEmissionTradingSchemeId: emission.regulatedEmissionTradingSchemeId ?? null,
      excludeFromReporting: emission.excludeFromReporting ?? false,
      excludeFromReportingReason: emission.excludeFromReportingReason ?? '',
      partOfEts: !!emission.regulatedEmissionTradingSchemeId,
      isFlag: flagged,
      flagCategory: flagged ? (flagCategory ?? '') : '',
    })
    setShowEts(!!emission.regulatedEmissionTradingSchemeId)
  }

  /** Seed the custom field values off the emission. Hidden fields are seeded too (but
   *  not rendered) so their existing values are preserved on save. */
  const seedCustomFieldValues = (emission: IEmissionDetail) => {
    const existingValues = emission.customFieldValues ?? []
    customFields.forEach((cf) => {
      const match = existingValues.find((v) => v.customFieldId === cf.id)
      formik.setFieldValue(cf.id, match?.value ?? '')
    })
  }

  useEffect(() => {
    setIsEditingCategory(false)
    categoryFormik.resetForm()
    if (dialogDetailsId) {
      emissionService
        .getOne(dialogDetailsId)
        .then((response: IEmissionDetail) => {
          setDialogDetails(response)
          seedFormFromEmission(response)
          // Full snapshot is safe here: the dialog only renders once dialogDetails is
          // set, so the form cannot have been touched before this seed lands.
          rebaseline()
        })
        .catch((err) => console.log(err))
    } else {
      setDialogDetails(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogDetailsId])

  useEffect(() => {
    subsidiaryService
      .getAll()
      .then((response) => setSubsidiaries(response))
      .catch((err) => console.log(err))
  }, [])

  useEffect(() => {
    if (!open) return
    locationService
      .getAllCountries()
      .then((response) => setCountries(response))
      .catch((err) => console.log(err))
    locationService
      .getActiveLocations()
      .then((response) => setLocations(response))
      .catch((err) => console.log(err))
    projectService
      .getAllActive()
      .then((response) => setProjects(response))
      .catch((err) => console.log(err))
    contactSupplierService
      .getActiveSuppliersLite(false)
      .then((response) => setContactSuppliers(response))
      .catch((err) => console.log(err))
    vehicleService
      .getAllActiveVehicles()
      .then((response) => setVehicles(response))
      .catch((err) => console.log(err))
    capitalGoodService
      .getAllActive()
      .then((response) => setCapitalGoods(response))
      .catch((err) => console.log(err))
    regulatedEmissionTradingSchemeService
      .getAll()
      .then((response) => setSchemes(response))
      .catch((err) => console.log(err))
    customFieldService
      .getAllActive(`&entryType=transaction`)
      .then((response) => setCustomFields(response))
      .catch((err) => console.log(err))
  }, [open])

  // Seed custom field values once both the emission and its custom fields are loaded.
  useEffect(() => {
    if (!dialogDetails || customFields.length === 0) return
    seedCustomFieldValues(dialogDetails)
    // Custom fields are refetched on every open, so this seed can land while the user is
    // already editing other fields: baseline only what it wrote, never their edits.
    rebaseline(customFields.map((cf) => `emission.${cf.id}`))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogDetails, customFields])

  const partOfEtsHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === 'true'
    formik.setFieldValue('partOfEts', value)
    if (!value) {
      formik.setFieldValue('regulatedEmissionTradingSchemeId', null)
    }
    setShowEts(value)
  }

  const excludeFromReportingHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === 'true'
    formik.setFieldValue('excludeFromReporting', value)
    if (!value) {
      formik.setFieldValue('excludeFromReportingReason', '')
    }
  }

  // One control carries the whole FLAG decision, so the pair can never contradict itself.
  const flagSelectionHandler = (value: string) => {
    const fields = flagFieldsFromSelectValue(value)
    formik.setFieldValue('isFlag', fields.isFlag)
    formik.setFieldValue('flagCategory', fields.flagCategory)
  }

  const handleSubsidiaryChange = (e: { target: { value: string } }) => {
    formik.handleChange(e)
    const subsidiary = subsidiaries?.find((s) => s.id === e.target.value)
    if (subsidiary?.countryId) {
      formik.setFieldValue('countryId', subsidiary.countryId)
    }
  }

  const cancelHandler = () => {
    setIsEditingCategory(false)
    categoryFormik.resetForm()
    // This dialog stays mounted with a fixed dialogDetailsId on the emission detail page,
    // so the seeding effect never re-runs on reopen. Discard any unsaved edits here, so a
    // reopened dialog shows the saved server state and an untouched form is never dirty.
    if (dialogDetails) {
      seedFormFromEmission(dialogDetails)
      seedCustomFieldValues(dialogDetails)
    }
    rebaseline()
    closeHandler()
  }

  const helperSx = { color: '#bf3333', marginLeft: '16px !important' }

  return (
    <>
      {dialogDetails && (
        <DialogWrapper
          open={open}
          onClose={cancelHandler}
          title="Edit Emission"
          cancelHandler={cancelHandler}
          submitHandler={formik.submitForm}
          isSubmitting={isSubmitting}
          isDirty={isDirty}
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* ── Category ───────────────────────────────────────── */}
            {/* Only spend-based and activity-based emissions can be re-mapped. Direct, product and
                hybrid emissions are edited per calculation on the emission detail page. */}
            {categoryEditMode && (
              <>
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.secondary', mt: 1, mb: -1 }}>
                  Category
                </Typography>
                {!isEditingCategory ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ color: 'text.primary', flex: 1 }}>
                      {currentCategoryLabel}
                    </Typography>
                    <IconButton
                      aria-label="Change category"
                      onClick={startCategoryEdit}
                      disabled={!!isReportPeriodLocked}
                    >
                      <EditOutlined fontSize="small" sx={{ color: 'primary.main' }} />
                    </IconButton>
                  </Stack>
                ) : categoryEditMode === 'SPEND' ? (
                  <SpendCategoryPicker
                    formik={categoryFormik}
                    calendarYear={categoryCalendarYear}
                  />
                ) : (
                  <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                      Currently {currentCategoryLabel}
                    </Typography>
                    <Link component="button" type="button" variant="caption" underline="hover"
                      onClick={cancelCategoryEdit} sx={{ cursor: 'pointer' }}>
                      Keep this
                    </Link>
                  </Stack>
                  <EmissionsFilterLogic
                    formik={categoryFormik}
                    calendarYear={categoryCalendarYear}
                    isEdit={false}
                    direction="column"
                    allowConversionSourceSelect
                    allowGroupSelect
                    allowCategorySelect
                  />
                  </>
                )}
              </>
            )}

            {/* ── Required section ───────────────────────────────── */}
            {hasMandatory && (
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.secondary', mt: 1, mb: -1 }}>
                Required
              </Typography>
            )}

            {subsidiaryVisible && (
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="subsidiaryId">
                  {screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}
                </InputLabel>
                <Select
                  id="subsidiaryId"
                  label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}
                  name="subsidiaryId"
                  value={formik.values.subsidiaryId || ''}
                  onChange={handleSubsidiaryChange}
                  error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                  sx={{ color: 'text.primary' }}
                >
                  {subsidiaries!
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((subsidiary, index) => (
                      <MenuItem key={index} value={subsidiary.id}>
                        {subsidiary.name}
                      </MenuItem>
                    ))}
                </Select>
                {formik.touched.subsidiaryId && formik.errors.subsidiaryId && (
                  <FormHelperText sx={helperSx}>
                    {(formik.errors.subsidiaryId as string) || null}
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('country') && getMode('country') === 'mandatory' && countries && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="countryId"
                  options={countries
                    .slice()
                    .sort((a, b) =>
                      a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)
                    )}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>
                    return (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} key={key} {...restProps}>
                        <img loading="lazy" width="20" src={option.imageUrl} alt="" />
                        {option.name}
                      </Box>
                    )
                  }}
                  value={countries?.find((c) => c.id === formik.values.countryId) || null}
                  onChange={(_e, value: ICountry | null) => formik.setFieldValue('countryId', value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField
                      label="Country"
                      {...params}
                      error={formik.touched.countryId && Boolean(formik.errors.countryId)}
                    />
                  )}
                />
                {formik.touched.countryId && formik.errors.countryId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.countryId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('location') && getMode('location') === 'mandatory' && locations && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="locationId"
                  options={locations.slice().sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))}
                  getOptionLabel={(option) => option.friendlyName}
                  value={locations?.find((l) => l.id === formik.values.locationId) || null}
                  onChange={(_e, value: ILocation | null) => formik.setFieldValue('locationId', value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField
                      label={screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })}
                      {...params}
                      error={formik.touched.locationId && Boolean(formik.errors.locationId)}
                    />
                  )}
                />
                {formik.touched.locationId && formik.errors.locationId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.locationId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('supplier') && getMode('supplier') === 'mandatory' && contactSuppliers && (
              <FormControl fullWidth variant="outlined">
                <SupplierPicker
                  id="contactSupplierId"
                  suppliers={contactSuppliers}
                  value={formik.values.contactSupplierId}
                  onChange={(id) => formik.setFieldValue('contactSupplierId', id ?? null)}
                  error={formik.touched.contactSupplierId && Boolean(formik.errors.contactSupplierId)}
                />
                {formik.touched.contactSupplierId && formik.errors.contactSupplierId && (
                  <FormHelperText sx={helperSx}>
                    {(formik.errors.contactSupplierId as string) || null}
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('vehicle') && getMode('vehicle') === 'mandatory' && vehicles && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="vehicleId"
                  options={vehicles.slice().sort((a, b) => a.customUniqueId.localeCompare(b.customUniqueId))}
                  getOptionLabel={(option) => option.customUniqueId}
                  value={vehicles?.find((v) => v.id === formik.values.vehicleId) || null}
                  onChange={(_e, value: IVehicleDetails | null) =>
                    formik.setFieldValue('vehicleId', value?.id ?? null)
                  }
                  renderInput={(params) => (
                    <TextField
                      label={screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })}
                      {...params}
                      error={formik.touched.vehicleId && Boolean(formik.errors.vehicleId)}
                    />
                  )}
                />
                {formik.touched.vehicleId && formik.errors.vehicleId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.vehicleId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('product') && getMode('product') === 'mandatory' && capitalGoods && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="capitalGoodId"
                  options={capitalGoods.slice().sort((a, b) => a.name.localeCompare(b.name))}
                  getOptionLabel={(option) => option.name}
                  value={capitalGoods?.find((g) => g.id === formik.values.capitalGoodId) || null}
                  onChange={(_e, value: ICapitalGood | null) =>
                    formik.setFieldValue('capitalGoodId', value?.id ?? null)
                  }
                  renderInput={(params) => (
                    <TextField
                      label={screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })}
                      {...params}
                      error={formik.touched.capitalGoodId && Boolean(formik.errors.capitalGoodId)}
                    />
                  )}
                />
                {formik.touched.capitalGoodId && formik.errors.capitalGoodId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.capitalGoodId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('project') && getMode('project') === 'mandatory' && userDetails?.organisation?.enableProjects && projects && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="projectId"
                  options={projects.slice().sort((a, b) => a.summary.localeCompare(b.summary))}
                  getOptionLabel={(option) => option.summary}
                  value={projects?.find((p) => p.id === formik.values.projectId) || null}
                  onChange={(_e, value: IProject | null) => formik.setFieldValue('projectId', value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField
                      label={screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })}
                      {...params}
                      error={formik.touched.projectId && Boolean(formik.errors.projectId)}
                    />
                  )}
                />
                {formik.touched.projectId && formik.errors.projectId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.projectId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('ets') && getMode('ets') === 'mandatory' && (
              <>
                <FormControl>
                  <Typography variant="body1" sx={{ color: 'text.primary', mt: 1 }}>
                    Are these emissions part of a Regulated Emission Trading Scheme?
                  </Typography>
                  <RadioGroup
                    name="partOfEts"
                    onChange={partOfEtsHandler}
                    value={formik.values.partOfEts}
                    row
                    sx={{ color: 'text.primary' }}
                  >
                    <FormControlLabel value={false} control={<Radio />} label="No" />
                    <FormControlLabel value={true} control={<Radio />} label="Yes" />
                  </RadioGroup>
                </FormControl>
                {showEts && schemes && (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="regulatedEmissionTradingSchemeId-label">
                      Regulated Emission Trading Scheme
                    </InputLabel>
                    <Select
                      id="regulatedEmissionTradingSchemeId"
                      labelId="regulatedEmissionTradingSchemeId-label"
                      name="regulatedEmissionTradingSchemeId"
                      value={formik.values.regulatedEmissionTradingSchemeId || ''}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.regulatedEmissionTradingSchemeId &&
                        Boolean(formik.errors.regulatedEmissionTradingSchemeId)
                      }
                      sx={{ color: 'text.primary' }}
                    >
                      {schemes.map((scheme, index) => (
                        <MenuItem key={index} value={scheme.id}>
                          {scheme.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.regulatedEmissionTradingSchemeId &&
                      formik.errors.regulatedEmissionTradingSchemeId && (
                        <FormHelperText sx={helperSx}>
                          {(formik.errors.regulatedEmissionTradingSchemeId as string) || null}
                        </FormHelperText>
                      )}
                  </FormControl>
                )}
              </>
            )}

            {!isHidden('excludeFromReporting') && getMode('excludeFromReporting') === 'mandatory' && (
              <>
                <FormControl>
                  <Typography variant="body1" sx={{ color: 'text.primary', mt: 1 }}>
                    Do you wish to exclude these emissions from analytics and reporting?
                  </Typography>
                  <RadioGroup
                    name="excludeFromReporting"
                    onChange={excludeFromReportingHandler}
                    value={formik.values.excludeFromReporting}
                    row
                    sx={{ color: 'text.primary' }}
                  >
                    <FormControlLabel value={true} control={<Radio />} label="Yes" />
                    <FormControlLabel value={false} control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
                {formik.values.excludeFromReporting && (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel htmlFor="excludeFromReportingReason">Reason for exclusion</InputLabel>
                    <OutlinedInput
                      id="excludeFromReportingReason"
                      label="Reason for exclusion"
                      name="excludeFromReportingReason"
                      value={formik.values.excludeFromReportingReason || ''}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.excludeFromReportingReason &&
                        Boolean(formik.errors.excludeFromReportingReason)
                      }
                      sx={{ color: 'text.primary' }}
                    />
                    {formik.touched.excludeFromReportingReason && formik.errors.excludeFromReportingReason && (
                      <FormHelperText sx={helperSx}>
                        {(formik.errors.excludeFromReportingReason as string) || null}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              </>
            )}

            {mandatoryCustomFields.map((cf) => (
              <FormControl key={`mandatory-cf-${cf.id}`} fullWidth variant="outlined">
                <InputLabel htmlFor={cf.id}>{cf.fieldName}</InputLabel>
                <OutlinedInput
                  id={cf.id}
                  label={cf.fieldName}
                  name={cf.id}
                  type={cf.fieldType === 'number' ? 'number' : 'text'}
                  value={formik.values[cf.id] ?? ''}
                  onChange={formik.handleChange}
                  error={formik.touched[cf.id] && Boolean(formik.errors[cf.id])}
                  sx={{ color: 'text.primary' }}
                />
                {formik.touched[cf.id] && formik.errors[cf.id] && (
                  <FormHelperText sx={helperSx}>{formik.errors[cf.id] as string}</FormHelperText>
                )}
              </FormControl>
            ))}

            {/* ── Optional section ───────────────────────────────── */}
            {hasOptional && (
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.secondary', mt: 1, mb: -1 }}>
                Optional
              </Typography>
            )}

            {!isHidden('country') && getMode('country') === 'optional' && countries && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="countryId-opt"
                  options={countries.slice().sort((a, b) => a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name))}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>
                    return (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} key={key} {...restProps}>
                        <img loading="lazy" width="20" src={option.imageUrl} alt="" />
                        {option.name}
                      </Box>
                    )
                  }}
                  value={countries?.find((c) => c.id === formik.values.countryId) || null}
                  onChange={(_e, value: ICountry | null) => formik.setFieldValue('countryId', value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField label="Country" {...params} error={formik.touched.countryId && Boolean(formik.errors.countryId)} />
                  )}
                />
                {formik.touched.countryId && formik.errors.countryId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.countryId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('location') && getMode('location') === 'optional' && locations && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="locationId-opt"
                  options={locations.slice().sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))}
                  getOptionLabel={(option) => option.friendlyName}
                  value={locations?.find((l) => l.id === formik.values.locationId) || null}
                  onChange={(_e, value: ILocation | null) => formik.setFieldValue('locationId', value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField label={screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })} {...params} error={formik.touched.locationId && Boolean(formik.errors.locationId)} />
                  )}
                />
                {formik.touched.locationId && formik.errors.locationId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.locationId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('supplier') && getMode('supplier') === 'optional' && contactSuppliers && (
              <FormControl fullWidth variant="outlined">
                <SupplierPicker
                  id="contactSupplierId-opt"
                  suppliers={contactSuppliers}
                  value={formik.values.contactSupplierId}
                  onChange={(id) => formik.setFieldValue('contactSupplierId', id ?? null)}
                  error={formik.touched.contactSupplierId && Boolean(formik.errors.contactSupplierId)}
                />
                {formik.touched.contactSupplierId && formik.errors.contactSupplierId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.contactSupplierId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('vehicle') && getMode('vehicle') === 'optional' && vehicles && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="vehicleId-opt"
                  options={vehicles.slice().sort((a, b) => a.customUniqueId.localeCompare(b.customUniqueId))}
                  getOptionLabel={(option) => option.customUniqueId}
                  value={vehicles?.find((v) => v.id === formik.values.vehicleId) || null}
                  onChange={(_e, value: IVehicleDetails | null) => formik.setFieldValue('vehicleId', value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField label={screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} {...params} error={formik.touched.vehicleId && Boolean(formik.errors.vehicleId)} />
                  )}
                />
                {formik.touched.vehicleId && formik.errors.vehicleId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.vehicleId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('product') && getMode('product') === 'optional' && capitalGoods && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="capitalGoodId-opt"
                  options={capitalGoods.slice().sort((a, b) => a.name.localeCompare(b.name))}
                  getOptionLabel={(option) => option.name}
                  value={capitalGoods?.find((g) => g.id === formik.values.capitalGoodId) || null}
                  onChange={(_e, value: ICapitalGood | null) => formik.setFieldValue('capitalGoodId', value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField label={screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} {...params} error={formik.touched.capitalGoodId && Boolean(formik.errors.capitalGoodId)} />
                  )}
                />
                {formik.touched.capitalGoodId && formik.errors.capitalGoodId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.capitalGoodId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('project') && getMode('project') === 'optional' && userDetails?.organisation?.enableProjects && projects && (
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="projectId-opt"
                  options={projects.slice().sort((a, b) => a.summary.localeCompare(b.summary))}
                  getOptionLabel={(option) => option.summary}
                  value={projects?.find((p) => p.id === formik.values.projectId) || null}
                  onChange={(_e, value: IProject | null) => formik.setFieldValue('projectId', value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField label={screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} {...params} error={formik.touched.projectId && Boolean(formik.errors.projectId)} />
                  )}
                />
                {formik.touched.projectId && formik.errors.projectId && (
                  <FormHelperText sx={helperSx}>{(formik.errors.projectId as string) || null}</FormHelperText>
                )}
              </FormControl>
            )}

            {!isHidden('ets') && getMode('ets') === 'optional' && (
              <>
                <FormControl>
                  <Typography variant="body1" sx={{ color: 'text.primary', mt: 1 }}>
                    Are these emissions part of a Regulated Emission Trading Scheme?
                  </Typography>
                  <RadioGroup name="partOfEts" onChange={partOfEtsHandler} value={formik.values.partOfEts} row sx={{ color: 'text.primary' }}>
                    <FormControlLabel value={false} control={<Radio />} label="No" />
                    <FormControlLabel value={true} control={<Radio />} label="Yes" />
                  </RadioGroup>
                </FormControl>
                {showEts && schemes && (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="regulatedEmissionTradingSchemeId-label-opt">Regulated Emission Trading Scheme</InputLabel>
                    <Select
                      id="regulatedEmissionTradingSchemeId-opt"
                      labelId="regulatedEmissionTradingSchemeId-label-opt"
                      name="regulatedEmissionTradingSchemeId"
                      value={formik.values.regulatedEmissionTradingSchemeId || ''}
                      onChange={formik.handleChange}
                      error={formik.touched.regulatedEmissionTradingSchemeId && Boolean(formik.errors.regulatedEmissionTradingSchemeId)}
                      sx={{ color: 'text.primary' }}
                    >
                      {schemes.map((scheme, index) => (
                        <MenuItem key={index} value={scheme.id}>{scheme.name}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.regulatedEmissionTradingSchemeId && formik.errors.regulatedEmissionTradingSchemeId && (
                      <FormHelperText sx={helperSx}>{(formik.errors.regulatedEmissionTradingSchemeId as string) || null}</FormHelperText>
                    )}
                  </FormControl>
                )}
              </>
            )}

            {!isHidden('excludeFromReporting') && getMode('excludeFromReporting') === 'optional' && (
              <>
                <FormControl>
                  <Typography variant="body1" sx={{ color: 'text.primary', mt: 1 }}>
                    Do you wish to exclude these emissions from analytics and reporting?
                  </Typography>
                  <RadioGroup name="excludeFromReporting" onChange={excludeFromReportingHandler} value={formik.values.excludeFromReporting} row sx={{ color: 'text.primary' }}>
                    <FormControlLabel value={true} control={<Radio />} label="Yes" />
                    <FormControlLabel value={false} control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
                {formik.values.excludeFromReporting && (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel htmlFor="excludeFromReportingReason-opt">Reason for exclusion</InputLabel>
                    <OutlinedInput
                      id="excludeFromReportingReason-opt"
                      label="Reason for exclusion"
                      name="excludeFromReportingReason"
                      value={formik.values.excludeFromReportingReason || ''}
                      onChange={formik.handleChange}
                      error={formik.touched.excludeFromReportingReason && Boolean(formik.errors.excludeFromReportingReason)}
                      sx={{ color: 'text.primary' }}
                    />
                    {formik.touched.excludeFromReportingReason && formik.errors.excludeFromReportingReason && (
                      <FormHelperText sx={helperSx}>{(formik.errors.excludeFromReportingReason as string) || null}</FormHelperText>
                    )}
                  </FormControl>
                )}
              </>
            )}

            {optionalCustomFields.map((cf) => (
              <FormControl key={`optional-cf-${cf.id}`} fullWidth variant="outlined">
                <InputLabel htmlFor={`${cf.id}-opt`}>{cf.fieldName}</InputLabel>
                <OutlinedInput
                  id={`${cf.id}-opt`}
                  label={cf.fieldName}
                  name={cf.id}
                  type={cf.fieldType === 'number' ? 'number' : 'text'}
                  value={formik.values[cf.id] ?? ''}
                  onChange={formik.handleChange}
                  error={formik.touched[cf.id] && Boolean(formik.errors[cf.id])}
                  sx={{ color: 'text.primary' }}
                />
                {formik.touched[cf.id] && formik.errors[cf.id] && (
                  <FormHelperText sx={helperSx}>{formik.errors[cf.id] as string}</FormHelperText>
                )}
              </FormControl>
            ))}

            {/* ── FLAG classification ────────────────────────────── */}
            {flagEnabled && (
              <>
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.secondary', mt: 1, mb: -1 }}>
                  FLAG classification
                </Typography>
                <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
                  <InputLabel htmlFor="flagCategory">FLAG category</InputLabel>
                  <Select
                    id="flagCategory"
                    label="FLAG category"
                    name="flagCategory"
                    value={flagSelectValueFrom(formik.values.isFlag, formik.values.flagCategory)}
                    onChange={(event) => flagSelectionHandler(event.target.value as string)}
                    sx={{ color: 'text.primary' }}
                  >
                    <MenuItem value="">Not FLAG-related</MenuItem>
                    {flagSelectValueFrom(formik.values.isFlag, formik.values.flagCategory) === FLAG_NO_CATEGORY_VALUE && (
                      <MenuItem value={FLAG_NO_CATEGORY_VALUE}>FLAG-related, no category recorded</MenuItem>
                    )}
                    {FLAG_CATEGORY_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>
        </DialogWrapper>
      )}
    </>
  )
}
